import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema, defaultObject} from '/vercel/path0/web/frontend/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/vercel/path0/web/frontend/markdoc/tags.js'
import * as nodes from '/vercel/path0/web/frontend/markdoc/nodes.js'
const functions = {};
const schema = {
tags: defaultObject(tags),
nodes: defaultObject(nodes),
functions: defaultObject(functions),
...defaultObject(config),
};

const tokenizer = new Markdoc.Tokenizer({"allowComments":true});

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Snowflake Logo, Brand Guidelines & Icons (2024)\ndescription: A quick guide on how to download the official Snowflake logo.\ndate: 2024-03-22T12:00:00.000Z\ntype: blog\nauthorNames: [ian]\nimage: \"public/blog-cards/snowflake-logo.jpeg\"\nhideCTA: true\nhideAuthorCard: false\nhideProductBanner: true\n---\n\nOftentimes, we find ourselves needing to download the official Snowflake company logo or reference their brand guidelines, for presentations or other related materials. It is surprisingly difficult to find, as Google search results are flooded with unofficial logos or third-party brand aggregator websites that aren’t trustworthy or up to date.\n\nInstead, we wanted to share how you can find & download the official Snowflake logos, brand guidelines & useful icons from their website.\n\n## How to find & download the official Snowflake Logo\n\nHead to [https://www.snowflake.com/brand-guidelines/](https://www.snowflake.com/brand-guidelines/) then click the **\"Logos\"** button to download all the logos:\n\n{% blog-post-image imageName=\"snowflake-brand-assets-location-v2\" alt=\"Snowflake logo download location\"/%}\n\n## Snowflake Logos (transparent, PNG, SVG and more!)\n\nOnce you click download from the link above, you’ll get a few different versions of the Snowflake logo in PNG, SVG, AI (Adobe Illustrator), and EPS (Encapsulated PostScript) formats:\n\n1. Full logo in blue & white.\n\n{% blog-post-image imageName=\"snowflake-full-logo\" alt=\"Snowflake full logo\" imgClassName=\"md:w-1/3\"/%}\n\n2. The logo bug in blue and white. Fun fact: a “logo bug” is the official name of a simplified version of a company’s logo. In this case, it’s the familiar part of the Snowflake logo without the “snowflake” text.\n\n{% blog-post-image imageName=\"snowflake-bug\" alt=\"Snowflake bug logo\" imgClassName=\"md:w-1/5\"/%}\n\n## Snowflake Brand Guidelines\n\nOn the [Snowflake brand guidelines](https://www.snowflake.com/brand-guidelines/) page, you'll find a bunch of useful information around Snowflake's brand guidelines:\n\n1. Their official typefaces\n2. The colour palette including the exact hex color codes\n3. UI elements\n4. Partner guidelines & logo usage instructions\n\n## Snowflake Icons\n\nWhen putting together an architecture diagram related to Snowflake infrastructure or data flows, it's incredibly helpful to have a set of icons to work with that highlight the relevant pieces of Snowflake technology or general data cloud components. Snowflake has an amazing set of icons that are perfect for this purpose, but tricky to find! \n\nOn the [Snowflake brand guidelines](https://www.snowflake.com/brand-guidelines/) page, there is a section to download their slides template:\n\n{% blog-post-image imageName=\"snowflake-slides-download\" alt=\"Snowflake icons download\"/%}\n\nIf you scroll down to slide ~70, you'll see over 5 slides containing a bunch of awesome icons you can work from. Here's a sample of what's there to give you some inspiration:\n\n{% blog-post-image imageName=\"snowflake-icons-1\" alt=\"Snowflake icons sample 1\" imgClassName=\"my-4\"/%}\n\n{% blog-post-image imageName=\"snowflake-icons-2\" alt=\"Snowflake icons sample 2\" imgClassName=\"my-4\"/%}\n\n{% blog-post-image imageName=\"snowflake-icons-3\" alt=\"Snowflake icons sample 3\" imgClassName=\"my-4\"/%}\n\n\n{% callout title=\"Always head to the Snowflake website for the most up to date resources\" type=\"warning\" %}\n\nThis page is only meant to serve as a guide for how and where to access the relevant Snowflake brand materials and guidelines. Please do not download or directly leverage content from this page, as it may become out of date.\n\n{% /callout %}\n\nHope that helps!\n\n";
const filepath = "/posts/snowflake-logo.md";
const tokens = tokenizer.tokenize(source);
const ast = Markdoc.parse(tokens, {slots: false});

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

async function getMarkdocData(context = {}) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    const tokens = tokenizer.tokenize(partials[key]);
    partials[key] = Markdoc.parse(tokens);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = await Markdoc.transform(ast, cfg);

  // Removes undefined
  return JSON.parse(
    JSON.stringify({
      content,
      frontmatter,
      file: {
        path: filepath,
      },
    })
  );
}

export async function getStaticProps(context) {
  return {
    props: {
      markdoc: await getMarkdocData(context),
    },
  };
}

export default function MarkdocComponent(props) {
  const markdoc = props.markdoc;
  // Only execute HMR code in development
  return renderers.react(markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
